import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Check, Clock, WifiOff } from "lucide-react";
import { sessionApi, type Destination } from "../../api/account";
import { ApiError } from "../../api/client";
import { AuthBadge, AuthHeading, FormError, Notice, Repeated, TextButton } from "../../components/account/AuthLayout";
import { PasswordField } from "../../components/account/PasswordField";
import { Button, CodeInput, Icon, StepIndicator } from "../../components/ui";
import { usePageTitle } from "../catalogue/usePageTitle";
import { errorMessage, fieldError, useSetSession } from "./session";

const RESEND_AFTER = 60;

export interface CodeStepsProps {
  /** Registering, or a forgotten password. */
  purpose: "sign_up" | "reset";
  destination: Destination;
  /** The number or address as the customer reads it: "+63 917 123 4567". */
  shown: string;
  /** Seconds before another code can be sent. */
  resendAfter: number;
  /** "Wrong number? Change it": back to the first screen. */
  onChangeDestination: () => void;
  returnTo: string;
  cartCount: number;
}

/** Seconds left, counting down once a second, and a way to start again. */
function useCountdown(start: number): [number, (seconds: number) => void] {
  const [seconds, setSeconds] = useState(start);
  useEffect(() => {
    if (seconds <= 0) return;
    const timer = setTimeout(() => setSeconds((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);
  return [seconds, setSeconds];
}

const clock = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const CODE_ERRORS = ["wrong_code", "code_expired", "too_many_attempts"];

/** What a refused code means, in the design system's words. */
function codeMessage(error: Error): string | undefined {
  if (!(error instanceof ApiError)) return errorMessage(error);
  if (error.code === "wrong_code") {
    const left = Number(error.details.attempts_left?.[0] ?? 0);
    return `That code isn't right. ${left} ${left === 1 ? "try" : "tries"} left.`;
  }
  if (error.code === "code_expired") return "That code has expired. Send a new code below.";
  if (error.code === "too_many_attempts") return "Too many wrong codes. Send a new code below.";
  return error.message;
}

/**
 * Enter the code, set a password, done (design system AuthFlow, PRD screens 13a and 13b).
 * Registering checks the code first and gets a sign-up token; a reset sends the code with
 * the new password, so a wrong code comes back from the password step to this one.
 */
export function CodeSteps({ purpose, destination, shown, resendAfter, onChangeDestination, returnTo, cartCount }: CodeStepsProps) {
  const signUp = purpose === "sign_up";
  const navigate = useNavigate();
  const setSession = useSetSession();
  const [step, setStep] = useState<"code" | "password" | "done">("code");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string>();
  const [locked, setLocked] = useState(false);
  const [signUpToken, setSignUpToken] = useState<string>();
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string>();
  const [seconds, restart] = useCountdown(resendAfter);
  const [resent, setResent] = useState(false);
  usePageTitle(step === "code" ? "Enter the code" : step === "password" ? (signUp ? "Set a password" : "Choose a new password") : signUp ? "Your account is ready" : "Password changed");

  const refuseCode = (error: Error) => {
    setCodeError(codeMessage(error));
    setLocked(error instanceof ApiError && error.code === "too_many_attempts");
  };

  const verify = useMutation({
    mutationFn: () => sessionApi.verifySignUpCode(destination, code),
    onSuccess: ({ sign_up_token }) => {
      setSignUpToken(sign_up_token);
      setStep("password");
    },
    onError: refuseCode,
  });

  const resend = useMutation({
    mutationFn: async () => {
      await (signUp ? sessionApi.sendSignUpCode(destination) : sessionApi.sendResetCode(destination));
    },
    onSuccess: () => {
      setCode("");
      setCodeError(undefined);
      setLocked(false);
      setResent(true);
      restart(RESEND_AFTER);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "code_recently_sent") restart(Number(error.details.retry_after?.[0] ?? RESEND_AFTER));
    },
  });

  const save = useMutation({
    mutationFn: () => (signUp ? sessionApi.register(signUpToken ?? "", password) : sessionApi.resetPassword(destination, code, password)),
    onSuccess: (session) => {
      setSession(session);
      setStep("done");
    },
    onError: (error) => {
      if (error instanceof ApiError && CODE_ERRORS.includes(error.code)) {
        refuseCode(error);
        setStep("code");
      } else {
        setPasswordError(fieldError(error, "password"));
      }
    },
  });

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    if (code.length < 6) return setCodeError("Enter all 6 digits");
    if (signUp) verify.mutate();
    else setStep("password");
  };

  const submitPassword = (event: FormEvent) => {
    event.preventDefault();
    const problem = !password ? "Choose a password" : password.length < 8 ? `Use at least 8 characters. This one has ${password.length}.` : undefined;
    setPasswordError(problem);
    if (!problem) save.mutate();
  };

  if (step === "code") {
    const resendFailed = resend.isError && !(resend.error instanceof ApiError && resend.error.code === "code_recently_sent");
    return (
      <>
        <StepIndicator current={0} className="mb-8" />
        <h1 className="mb-3 type-h1 [font-size:var(--text-2xl)] tracking-tight">Enter the code</h1>
        <p className="mb-1 type-body text-text-body">
          We sent a code to <Repeated>{shown}</Repeated>.
        </p>
        <div className="mb-7">
          <TextButton onClick={onChangeDestination}>{"email_address" in destination ? "Wrong email? Change it" : "Wrong number? Change it"}</TextButton>
        </div>
        <form onSubmit={submitCode} noValidate>
          <CodeInput
            value={code}
            disabled={locked || verify.isPending}
            error={codeError}
            hint={"email_address" in destination ? "Check your inbox, and your spam folder." : "It's in the SMS from KALIKAYA."}
            onChange={(value) => {
              setCode(value);
              if (!locked) setCodeError(undefined);
            }}
            autoFocus
          />
          {resendFailed && (
            <Notice tone="danger" icon={WifiOff}>
              We couldn't send a new code to <Repeated>{shown}</Repeated>. Check your signal, then try again.
            </Notice>
          )}
          <Button type="submit" fullWidth size="lg" disabled={locked || verify.isPending || resend.isPending} className="mt-6">
            {verify.isPending ? "Checking the code…" : "Verify"}
          </Button>
        </form>
        <div className="mt-5 flex min-h-11 flex-wrap items-center gap-3 type-small text-text-muted">
          {resend.isPending ? (
            <span>Sending a new code…</span>
          ) : seconds > 0 ? (
            <span>
              Send a new code in <span className="type-sku tabular-nums">{clock(seconds)}</span>
            </span>
          ) : (
            <TextButton onClick={() => resend.mutate()}>{resendFailed ? "Try sending again" : "Send a new code"}</TextButton>
          )}
          {resent && seconds > 0 && !resend.isPending && (
            <span className="inline-flex items-center gap-1 text-success-700">
              <Icon icon={Check} size={14} strokeWidth={2.4} />
              New code sent
            </span>
          )}
        </div>
      </>
    );
  }

  if (step === "password") {
    const expired = save.error instanceof ApiError && save.error.code === "sign_up_expired";
    return (
      <>
        <StepIndicator current={1} className="mb-8" />
        <AuthHeading title={signUp ? "Set a password" : "Choose a new password"}>
          You'll use it with <Repeated>{shown}</Repeated> to sign in.
        </AuthHeading>
        <form onSubmit={submitPassword} noValidate>
          <PasswordField
            value={password}
            onChange={(value) => {
              setPassword(value);
              setPasswordError(undefined);
            }}
            autoComplete="new-password"
            hint="At least 8 characters"
            error={passwordError}
            disabled={save.isPending}
            autoFocus
          />
          <Button type="submit" fullWidth size="lg" iconRight={save.isPending ? undefined : ArrowRight} disabled={save.isPending} className="mt-6">
            {save.isPending ? (signUp ? "Creating your account…" : "Saving…") : signUp ? "Create account" : "Save password"}
          </Button>
          {expired ? (
            <Notice tone="danger" icon={Clock} action={<TextButton onClick={onChangeDestination}>Start again</TextButton>}>
              That took too long, so the code no longer works.
            </Notice>
          ) : (
            !passwordError && <FormError>{errorMessage(save.error)}</FormError>
          )}
        </form>
      </>
    );
  }

  const next =
    returnTo === "/checkout"
      ? { to: "/checkout", label: "Back to checkout" }
      : cartCount > 0
        ? { to: "/cart", label: "Back to your basket" }
        : { to: returnTo, label: "Continue shopping" };
  return (
    <>
      <StepIndicator current={2} className="mb-8" />
      <AuthBadge icon={Check} />
      <AuthHeading title={signUp ? "Your account is ready" : "Password changed"}>
        You're signed in as <Repeated>{shown}</Repeated>.
      </AuthHeading>
      <Button fullWidth size="lg" iconRight={ArrowRight} onClick={() => navigate(next.to, { replace: true })}>
        {next.label}
      </Button>
    </>
  );
}
